import { CryptoIcon, FiatFlag, ChevronDown, InfoIcon } from '../ui/icons';
import { useT } from '../../i18n';
import { AnimatedNumber } from '../ui/AnimatedNumber';
import { Spinner } from '../ui/Spinner';
import type { FieldLocksConfig } from '@nowramp/sdk';

type MaybeFieldLocks = FieldLocksConfig | null | undefined;

export interface ReceiveCardProps {
  amount: string;
  currencyCode: string;
  chainCode?: string;
  chainDisplay?: string;
  isBuy: boolean;
  onOpenModal: () => void;
  displayRate: string;
  assetBaseUrl: string;
  quotesLoading: boolean;
  onOpenRateBreakdown: () => void;
  fieldLocks?: MaybeFieldLocks;
  spendNumeric: number;
  getChainIconUrl?: (chain: string, base: string) => string;
  autoSelectPending?: boolean;
  rateFlash?: boolean;
}

export function ReceiveCard({
  amount,
  currencyCode,
  chainCode,
  chainDisplay,
  isBuy,
  onOpenModal,
  displayRate,
  assetBaseUrl,
  quotesLoading,
  onOpenRateBreakdown,
  fieldLocks,
  spendNumeric,
  getChainIconUrl,
  autoSelectPending,
  rateFlash,
}: ReceiveCardProps) {
  const t = useT();
  return (
    <div
      className="p-4 card-hover"
      style={{
        borderRadius: 'var(--t-card-radius)',
        border: '1.5px solid var(--t-card-border)',
        background: 'transparent',
      }}
    >
      <div className="flex flex-col gap-[20px]">
      <div className="text-[16px]" style={{ color: 'var(--t-text-secondary)', lineHeight: '16px' }}>
        {t('receive.label')}
      </div>
      <div className="flex flex-col gap-[8px]">
      <div className="flex items-start justify-between">
        <div className={rateFlash ? 'rate-flash' : ''}>
          <AnimatedNumber
            value={spendNumeric > 0 ? amount : '0'}
            height={32}
            className="font-medium"
            style={{
              color: spendNumeric > 0 ? 'var(--t-text)' : 'var(--t-text-muted)',
              fontSize: 24,
            }}
          />
        </div>
        {isBuy ? (
          <div className="flex flex-col items-end shrink-0">
            <button
              onClick={onOpenModal}
              className="flex items-center gap-[4px] transition-colors"
              style={{
                background: 'var(--t-pill-solid-bg)',
                borderRadius: 'var(--t-pill-radius)',
                paddingLeft: 4,
                paddingRight: 8,
                paddingTop: 4,
                paddingBottom: 4,
                border: '1px solid var(--t-pill-solid-border)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
              }}
              disabled={!!fieldLocks?.destinationCurrency?.locked}
            >
              <CryptoIcon ticker={currencyCode} size={24} baseUrl={assetBaseUrl} />
              <span className="text-[14px] font-semibold" style={{ color: 'var(--t-text)', letterSpacing: '0.14px', lineHeight: '24px' }}>
                {currencyCode}
              </span>
              <ChevronDown />
            </button>
          </div>
        ) : (
          <button
            onClick={onOpenModal}
            className="flex items-center gap-[4px] shrink-0 transition-colors"
            style={{
              background: 'var(--t-pill-solid-bg)',
              borderRadius: 'var(--t-pill-radius)',
              paddingLeft: 4,
              paddingRight: 8,
              paddingTop: 4,
              paddingBottom: 4,
              border: '1px solid var(--t-pill-solid-border)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
            }}
            disabled={!!fieldLocks?.sourceCurrency?.locked}
          >
            <FiatFlag currencyCode={currencyCode} size={24} baseUrl={assetBaseUrl} />
            <span className="text-[14px] font-semibold" style={{ color: 'var(--t-text)', letterSpacing: '0.14px', lineHeight: '24px' }}>
              {currencyCode}
            </span>
            <ChevronDown />
          </button>
        )}
      </div>

      {/* Bottom row: rate + chain info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[4px]">
          {quotesLoading && !autoSelectPending ? (
            <>
              <Spinner size={12} />
              <span className="text-[11px] font-medium" style={{ color: 'var(--t-text-muted)', letterSpacing: '0.22px', lineHeight: '12px' }}>
                {t('receive.fetchingRate')}
              </span>
            </>
          ) : (
            <>
              <span className="text-[11px] font-medium" style={{ color: 'var(--t-text-muted)', letterSpacing: '0.22px', lineHeight: '12px' }}>
                {displayRate || '\u00A0'}
              </span>
              {displayRate && (
                <button
                  onClick={onOpenRateBreakdown}
                  className="flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity"
                >
                  <InfoIcon size={12} />
                </button>
              )}
            </>
          )}
        </div>
        {isBuy && chainCode && getChainIconUrl && (
          <div className="flex items-center gap-1">
            <div className="rounded-full" style={{ width: 12, height: 12, background: '#ffffff' }}>
              <img
                src={getChainIconUrl(chainCode, assetBaseUrl)}
                alt={chainCode}
                width={12}
                height={12}
                className="rounded-full"
                style={{ width: 12, height: 12 }}
              />
            </div>
            <span className="text-[11px]" style={{ color: 'var(--t-text-secondary)', letterSpacing: '0.22px', lineHeight: '12px' }}>
              {chainDisplay}
            </span>
          </div>
        )}
      </div>
      </div>
      </div>
    </div>
  );
}
